import { forwardRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../lib/cn';
import { t } from '../lib/i18n';

/** Primitivas de UI reusables — colores 100% desde tokens (clases mapeadas). */

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  fullWidth?: boolean;
  /** Renderiza el hijo como raíz (ej. <Link>) conservando estilos. */
  asChild?: boolean;
}

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-ink hover:bg-brand-2 disabled:bg-brand/50 disabled:cursor-not-allowed',
  secondary:
    'border border-line bg-surface text-ink hover:border-brand hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed',
  ghost: 'text-ink hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading, fullWidth, asChild, disabled, className, children, ...props },
  ref,
) {
  const classes = cn(
    'inline-flex h-10 items-center justify-center gap-2 rounded-token px-4 text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    buttonVariants[variant],
    fullWidth && 'w-full',
    className,
  );

  // Con asChild delegamos al hijo (ej. <Link>): no inyectamos el spinner ni
  // atributos propios de <button> para no romper el elemento envuelto.
  if (asChild) {
    return (
      <Slot className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button ref={ref} disabled={disabled || loading} className={classes} {...props}>
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
});

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, id, className, type, ...props },
  ref,
) {
  const fieldId = id ?? props.name;
  // Campos de contraseña: mostramos un "ojito" para alternar visible/oculto.
  const isPassword = type === 'password';
  const [visible, setVisible] = useState(false);
  const effectiveType = isPassword && visible ? 'text' : type;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={fieldId}
          type={effectiveType}
          aria-invalid={error ? true : undefined}
          className={cn(
            'block w-full rounded-token border bg-surface px-3 py-2 text-sm text-ink',
            'placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand',
            error ? 'border-danger' : 'border-line',
            isPassword && 'pr-10',
            className,
          )}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? t('ui.password.hide') : t('ui.password.show')}
            aria-pressed={visible}
            title={visible ? t('ui.password.hide') : t('ui.password.show')}
            className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-token text-muted transition-colors hover:text-ink focus-visible:text-brand focus-visible:outline-none"
          >
            {visible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
        )}
      </div>
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <div className="text-xs text-muted">{hint}</div>
      ) : null}
    </div>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: ReactNode;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, id, className, ...props },
  ref,
) {
  const fieldId = id ?? props.name;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(
          'block w-full rounded-token border bg-surface px-3 py-2 text-sm text-ink',
          'placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand',
          error ? 'border-danger' : 'border-line',
          className,
        )}
        {...props}
      />
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <div className="text-xs text-muted">{hint}</div>
      ) : null}
    </div>
  );
});

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, id, className, children, ...props },
  ref,
) {
  const fieldId = id ?? props.name;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(
          'block w-full rounded-token border bg-surface px-3 py-2 text-sm text-ink',
          'focus:outline-none focus:ring-2 focus:ring-brand',
          error ? 'border-danger' : 'border-line',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
});

/** Bloque de error de formulario (no ligado a un campo). */
export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="alert"
      className="rounded-token border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {children}
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Etiqueta accesible cuando no hay <label> visible asociado. */
  'aria-label'?: string;
  id?: string;
  className?: string;
}

/**
 * Switch accesible (role="switch"). Colores desde tokens: `bg-brand` encendido,
 * `bg-line` apagado. Sin dependencias nuevas (no hay @radix-ui/react-switch).
 */
export function Toggle({
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
  ...aria
}: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={aria['aria-label']}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand' : 'bg-line',
        className,
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-surface shadow-sm transition-transform',
          checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('animate-spin text-brand', className)} size={20} />;
}

/** Pantalla de carga a pantalla completa (bootstrap de sesión). */
export function FullPageSpinner() {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg">
      <Spinner className="h-8 w-8" />
    </div>
  );
}
